import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ContentValue } from "@fabric-types/svc-content";
import { Store } from "@ngrx/store";
import { MessageSnackBarService } from "@obg/mat.ui.core";
import { PHONE_VERIFICATION_ACTION_TYPE, PhoneVerificationAction } from "@obg/ngx.common";
import { CoreState, CustomerManagementAction, CustomerManagementSelector, MessageType } from "@obg/ngx.core";
import { DocumentRef } from "@obg/ngx.global.evo";
import { OBG_VALIDATORS } from "@obg/ngx.ui.common";
import { merge, Observable, Subscription } from "rxjs";
import { filter, map, tap } from "rxjs/operators";

const TRANSLATION_PREFIX = "phone-verification";

@Component({
	selector: "obg-m-phone-verification-container",
	templateUrl: "./phone-verification.container.html",
	styleUrls: ["./phone-verification.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class PhoneVerificationContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-phone-verification") hostClass = true;

	data$$: Subscription | undefined;
	callingCodes: string[] | undefined;
	isPhoneNumberVerified = false;
	phoneExtension: UntypedFormControl | undefined;
	phoneNumberFormGroup: UntypedFormGroup | undefined;
	verifyCodeFormGroup: UntypedFormGroup | undefined;

	constructor(
		private store: Store<CoreState>,
		private formBuilder: UntypedFormBuilder,
		private phoneVerificationAction: PhoneVerificationAction,
		private customerManagementAction: CustomerManagementAction,
		private changeDetector: ChangeDetectorRef,
		private messageSnackBarService: MessageSnackBarService,
		private documentRef: DocumentRef
	) {}

	ngOnInit() {
		this.phoneNumberFormGroup = this.formBuilder.group({
			phoneExtension: new UntypedFormControl(),
			phoneNumber: this.formBuilder.control("", [
				Validators.required,
				Validators.minLength(6),
				Validators.maxLength(15),
				OBG_VALIDATORS.numeric,
			]),
		});

		this.verifyCodeFormGroup = this.formBuilder.group({
			verificationCode: this.formBuilder.control("", [
				Validators.required,
				Validators.minLength(4),
				Validators.maxLength(4),
				OBG_VALIDATORS.numeric,
			]),
		});

		const callingCodes$ = new Observable(
			this.documentRef.obgEventBus.retrieve(ContentValue.getCountryCallingCodes)
		).pipe(tap((codes) => (this.callingCodes = codes)));

		const customerPhoneNumber$ = this.store.select(CustomerManagementSelector.getCustomer).pipe(
			filter((customer) => !!customer),
			map((customer) => customer!.subscriptions),
			tap((subscriptions) => {
				this.isPhoneNumberVerified = !!subscriptions.isPhoneNumberVerified;
				this.phoneNumberFormGroup!.patchValue({
					phoneExtension: subscriptions.phoneExtension,
					phoneNumber: subscriptions.phoneNumber,
				});
			})
		);

		const phoneVerificationPostSuccess$ = this.messageSnackBarService.presentOnDispatch(
			{
				context: PHONE_VERIFICATION_ACTION_TYPE.requestCodeSuccess,
				messageType: MessageType.Success,
			},
			{ translationPrefix: TRANSLATION_PREFIX }
		);

		const phoneVerificationVerifySuccess$ = this.messageSnackBarService
			.presentOnDispatch(
				{
					context: PHONE_VERIFICATION_ACTION_TYPE.verifyCodeSuccess,
					messageType: MessageType.Success,
				},
				{ translationPrefix: TRANSLATION_PREFIX }
			)
			.pipe(tap(() => this.store.dispatch(this.customerManagementAction.getCustomer())));

		const phoneVerificationPostError$ = this.messageSnackBarService.presentOnDispatch(
			{
				context: PHONE_VERIFICATION_ACTION_TYPE.requestCode,
				messageType: MessageType.Error,
			},
			{ translationPrefix: TRANSLATION_PREFIX }
		);

		const phoneVerificationVerifyError$ = this.messageSnackBarService.presentOnDispatch(
			{
				context: PHONE_VERIFICATION_ACTION_TYPE.verifyCode,
				messageType: MessageType.Error,
			},
			{ translationPrefix: TRANSLATION_PREFIX }
		);

		this.data$$ = merge(
			phoneVerificationPostSuccess$,
			phoneVerificationVerifySuccess$,
			phoneVerificationPostError$,
			phoneVerificationVerifyError$,
			customerPhoneNumber$,
			callingCodes$
		).subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	submitPhoneNumberForm() {
		if (!this.phoneNumberFormGroup) {
			return;
		}

		const phoneNumber = `+${this.phoneNumberFormGroup.value.phoneExtension} ${this.phoneNumberFormGroup.value.phoneNumber}`;
		this.store.dispatch(this.phoneVerificationAction.requestCode(phoneNumber));
	}

	submitVerifyForm() {
		if (!this.verifyCodeFormGroup) {
			return;
		}

		const verificationCode = this.verifyCodeFormGroup.value.verificationCode;
		this.store.dispatch(this.phoneVerificationAction.verifyCode(verificationCode));
	}

	callingCodeTrackBy(_index: number, callingCode: string): string {
		return callingCode;
	}
}
